/* eslint-disable react/jsx-one-expression-per-line */
import React from 'react';
import moment from 'moment';
import { DateTime } from 'luxon';
import { TaskHelpers } from 'ontraccr-common';

import { ReportColumn } from '../ReportColumnHeader';

import { applyNightShiftPremium, calculateCostFromHours } from '../../../helpers/payroll';
import { formatProjectLabelFromCompanySettings } from '../../../projects/projectHelpers';
import { msToHours } from '../../../helpers/time';

import {
  getDivision,
  getDerivedTasksInRange,
  isGoodTask,
} from '../../reportHelpers';

import { getRelevantWageAndBillingRate } from '../../../helpers/wageHelpers';
import config from '../../../config';

export default {};

export const computeData = ({
  onDataChanged,
  onFiltersChanged,
  settings,
  isRounded,
  userDivisions,
  timeEntryUserMap = {},
  users = [],
  enabledFilters,
  projects,
  costcodes,
  phases,
  workType,
  zeroHoursEnabled,
  isValidTask,
  getAggregateKey,
  divisionMap,
  projectIdMap,
  costcodeIdMap,
  classIdMap,
  phaseMap,
  dateRange,
  showActiveUsersOnly = false,
  entityOTProfileMap = {},
}) => {
  const data = [];
  const dateAggregate = {};
  const userDateCostCodeSet = new Set();
  const filteredUsers = showActiveUsersOnly ? users.filter((user) => user.active) : users;

  // zero hours
  if (zeroHoursEnabled) {
    const usersWithNoHours = [];

    filteredUsers.forEach((user) => {
      if (!user.active) return;
      const tasks = timeEntryUserMap[user?.id] ?? [];

      const validTasks = tasks.filter((task) => (
        isGoodTask(task)
          && isValidTask(task, true)
      ));

      // if user has any valid tasks, then they have time logged
      if (!validTasks.length) {
        usersWithNoHours.push(user);
      }
    });

    const aggKey = getAggregateKey('None', 'None', 'None', null, null);

    const usersWithNoHoursData = usersWithNoHours.map((user) => ({
      name: user.name,
      employeeId: user.employeeId ?? '',
      division: getDivision({
        project: {}, divisionMap, userDivisions, user,
      }),
      key: user.id + aggKey,
      notes: 'No Hours Logged',
      rawNotes: 'No Hours Logged',
      hasNightShift: false,
    }));

    onDataChanged(usersWithNoHoursData);
    onFiltersChanged({
      users: filteredUsers,
      projects,
      costcodes,
      phases,
    });

    return usersWithNoHoursData;
  }

  // non zero-hours
  filteredUsers.forEach((user) => {
    const tasks = timeEntryUserMap[user?.id] ?? [];
    // only showing work for employee payroll report
    getDerivedTasksInRange({
      tasks,
      companySettings: settings,
      settingsMap: entityOTProfileMap,
      dateRange,
      shouldIncludeZeroTimePTO: true,
      isRounded,
    }).filter((t) => workType.has(t.type)).forEach((task) => {
      if (!isValidTask(task)) return;
      const project = projectIdMap[task.projectId];
      const phase = phaseMap[task.phaseId];
      const costcode = costcodeIdMap[task.costcodeId];
      const unionClass = classIdMap[task.classId];

      const date = TaskHelpers.getTaskDate(task)?.toLocaleString(DateTime.DATE_MED) ?? '';

      const runTime = task.endTime - task.startTime;

      const state = task.state || 'Not Submitted';

      const projectLabel = project ? formatProjectLabelFromCompanySettings({
        name: project.name,
        number: project.number,
        settings,
      }) : null;

      if (!(user.id in dateAggregate)) {
        const dataObject = {
          name: user.name,
          employeeId: user.employeeId ?? '',
          wage: user.wage,
          project: projectLabel,
          costcode: costcode ? costcode.code : null,
          runTime,
          regularHours: runTime,
          otHours: 0,
          doubleOtHours: 0,
          ptoHours: 0,
          saturdayHours: 0,
          saturdayOTHours: 0,
          saturdayDoubleOTHours: 0,
          sundayHours: 0,
          sundayOTHours: 0,
          sundayDoubleOTHours: 0,
          totalHours: 0,
          totalCost: '$0.00',
          date,
          unapproved: runTime,
          approved: 0,
          state,
          serviceLocation: task.serviceLocation,
          type: task.type,
          hasNightShift: !!task.isNightShift,
        };

        dateAggregate[user.id] = {
          ...dataObject,
          values: {},
        };
      }

      const userDateCostCodeKey = `${user.id}-${date}-${costcode?.id}`;

      const costCode = costcode ? costcode.code : 'None';
      const fullPhase = phase?.name ?? 'None';
      const aggKey = getAggregateKey(
        projectLabel ?? 'None',
        fullPhase,
        costCode,
        task.serviceLocation,
        task.id,
      );
      const {
        hourlyWage: resolvedHourlyWage,
        dailyWage: resolvedDailyWage,
      } = getRelevantWageAndBillingRate({
        task, user, costcode, unionClass,
      });

      let wage = resolvedHourlyWage ?? 0;
      let dailyWage = 0;
      // Daily wage is flat per user+date+costcode; only charge once per combo
      if (resolvedDailyWage != null) {
        wage = 0;
        if (!userDateCostCodeSet.has(userDateCostCodeKey)) {
          dailyWage = resolvedDailyWage;
          userDateCostCodeSet.add(userDateCostCodeKey);
        }
      }

      if (!(aggKey in dateAggregate[user.id].values)) {
        dateAggregate[user.id].values[aggKey] = {
          employeeId: user.employeeId ?? '',
          wage,
          dailyWage,
          regularHours: 0,
          breakHours: 0,
          otHours: 0,
          ptoHours: 0,
          satOTHours: 0,
          sunOTHours: 0,
          totalHours: 0,
          unapproved: 0,
          approved: 0,
          costcode: costcode ? costcode.code : null,
          project: projectLabel,
          date,
          phase: fullPhase,
          key: user.id + aggKey,
          serviceLocation: task.serviceLocation,
          regOT: 0,
          doubleOT: 0,
          saturday: 0,
          saturdayOT: 0,
          saturdayDoubleOT: 0,
          sunday: 0,
          sundayOT: 0,
          sundayDoubleOT: 0,
          notes: [],
          rawNotes: '',
          weightedAverageWage: 0,
          type: task.type,
          hasNightShift: !!task.isNightShift,
          // Track night shift hours separately
          regularHoursNightShift: 0,
          regOTNightShift: 0,
          doubleOTNightShift: 0,
          saturdayNightShift: 0,
          saturdayOTNightShift: 0,
          saturdayDoubleOTNightShift: 0,
          sundayNightShift: 0,
          sundayOTNightShift: 0,
          sundayDoubleOTNightShift: 0,
          totalDailyCost: 0,
          division: getDivision({
            project, divisionMap, userDivisions, user,
          }),
          userSettings: entityOTProfileMap[user.id] ?? settings,
        };
      }
      const isWeekday = !task.isSaturday && !task.isSunday;
      const hrReg = isWeekday ? msToHours(task.regularTime) : 0;
      const hourOT = isWeekday ? msToHours(task.overtime) : 0;
      const hourDouble = isWeekday ? msToHours(task.doubleOT) : 0;
      const hourPTO = msToHours(task.pto || 0);
      const satHrReg = msToHours(task.saturdayTime || 0);
      const satHourOT = msToHours(task.saturdayOT || 0);
      const satHourDouble = msToHours(task.saturdayDoubleOT || 0);
      const sunHrReg = msToHours(task.sundayTime || 0);
      const sunHourOT = msToHours(task.sundayOT || 0);
      const sunHourDouble = msToHours(task.sundayDoubleOT || 0);
      const hrBreak = msToHours(task.breakTime || 0);

      const oldNote = dateAggregate[user.id].values[aggKey].notes;

      const totalHours = (
        hrReg + hourOT + hourDouble + hourPTO
        + satHrReg + sunHrReg + satHourOT + satHourDouble
        + sunHourOT + sunHourDouble
      );

      dateAggregate[user.id].values[aggKey].weightedAverageWage += wage * totalHours;

      if (task.state === 'approved') {
        dateAggregate[user.id].values[aggKey].approved += totalHours;
      } else {
        dateAggregate[user.id].values[aggKey].unapproved += totalHours;
      }

      if (task.type === 'break') {
        const breakHours = msToHours(runTime);
        dateAggregate[user.id].values[aggKey].breakHours += breakHours;
        dateAggregate[user.id].values[aggKey].totalHours += breakHours;
      } else {
        // Track night shift hours separately for cost calculation, but also add to
        // display fields so they appear combined in the report
        if (task.isNightShift) {
          dateAggregate[user.id].values[aggKey].hasNightShift = true;
          dateAggregate[user.id].values[aggKey].regularHoursNightShift += hrReg;
          dateAggregate[user.id].values[aggKey].regOTNightShift += hourOT;
          dateAggregate[user.id].values[aggKey].doubleOTNightShift += hourDouble;
          dateAggregate[user.id].values[aggKey].saturdayNightShift += satHrReg;
          dateAggregate[user.id].values[aggKey].saturdayOTNightShift += satHourOT;
          dateAggregate[user.id].values[aggKey].saturdayDoubleOTNightShift += satHourDouble;
          dateAggregate[user.id].values[aggKey].sundayNightShift += sunHrReg;
          dateAggregate[user.id].values[aggKey].sundayOTNightShift += sunHourOT;
          dateAggregate[user.id].values[aggKey].sundayDoubleOTNightShift += sunHourDouble;
        }

        // Display fields always accumulate all hours (night shift + regular combined)
        dateAggregate[user.id].values[aggKey].regularHours += hrReg;
        dateAggregate[user.id].values[aggKey].regOT += hourOT;
        dateAggregate[user.id].values[aggKey].doubleOT += hourDouble;
        dateAggregate[user.id].values[aggKey].saturday += satHrReg;
        dateAggregate[user.id].values[aggKey].saturdayOT += satHourOT;
        dateAggregate[user.id].values[aggKey].saturdayDoubleOT += satHourDouble;
        dateAggregate[user.id].values[aggKey].sunday += sunHrReg;
        dateAggregate[user.id].values[aggKey].sundayOT += sunHourOT;
        dateAggregate[user.id].values[aggKey].sundayDoubleOT += sunHourDouble;

        // Total hours (both night shift and regular) for display
        dateAggregate[user.id].values[aggKey].otHours += hourOT + hourDouble;
        dateAggregate[user.id].values[aggKey].satOTHours += satHourOT + satHourDouble;
        dateAggregate[user.id].values[aggKey].sunOTHours += sunHourOT + sunHourDouble;
        dateAggregate[user.id].values[aggKey].totalHours += totalHours;
        dateAggregate[user.id].values[aggKey].totalDailyCost += dailyWage;
        dateAggregate[user.id].values[aggKey].ptoHours += hourPTO;
        dateAggregate[user.id].values[aggKey].breakHours += hrBreak;
      }

      if (task.note) {
        if (oldNote.length > 0) {
          dateAggregate[user.id].values[aggKey].rawNotes += '\n\n';
          dateAggregate[user.id].values[aggKey].notes.push(<><br /><br /></>);
        }
        dateAggregate[user.id].values[aggKey].rawNotes += task.note;
        dateAggregate[user.id].values[aggKey].notes.push(<span key={task.id}>{task.note}</span>);
      }
    });
  });

  let totalOfTotals = 0;

  Object.values(dateAggregate).forEach((obj) => {
    Object.values(obj.values).forEach((hours) => {
      const relevantWage = hours.totalHours
        ? hours.weightedAverageWage / hours.totalHours
        : 0;

      // Calculate regular hours cost (without night shift premium).
      // Display fields include night shift hours combined, so subtract them out here
      // to avoid double-counting since night shift cost is calculated separately below.
      const regularHours = {
        regularHours: hours.regularHours - hours.regularHoursNightShift,
        ptoHours: hours.ptoHours,
        regOT: hours.regOT - hours.regOTNightShift,
        doubleOT: hours.doubleOT - hours.doubleOTNightShift,
        saturday: hours.saturday - hours.saturdayNightShift,
        saturdayOT: hours.saturdayOT - hours.saturdayOTNightShift,
        saturdayDoubleOT: hours.saturdayDoubleOT - hours.saturdayDoubleOTNightShift,
        sunday: hours.sunday - hours.sundayNightShift,
        sundayOT: hours.sundayOT - hours.sundayOTNightShift,
        sundayDoubleOT: hours.sundayDoubleOT - hours.sundayDoubleOTNightShift,
      };
      const regularHoursCost = calculateCostFromHours(regularHours, relevantWage, hours.userSettings);

      // Calculate night shift hours cost (with night shift premium applied to base wage)
      const wageWithNightShift = applyNightShiftPremium(
        relevantWage,
        { isNightShift: true },
        hours.userSettings,
      );

      const nightShiftHours = {
        regularHours: hours.regularHoursNightShift,
        regOT: hours.regOTNightShift,
        doubleOT: hours.doubleOTNightShift,
        saturday: hours.saturdayNightShift,
        saturdayOT: hours.saturdayOTNightShift,
        saturdayDoubleOT: hours.saturdayDoubleOTNightShift,
        sunday: hours.sundayNightShift,
        sundayOT: hours.sundayOTNightShift,
        sundayDoubleOT: hours.sundayDoubleOTNightShift,
      };
      const nightShiftHoursCost = calculateCostFromHours(
        nightShiftHours,
        wageWithNightShift,
        hours.userSettings,
      );

      let totalCost = regularHoursCost + nightShiftHoursCost + hours.totalDailyCost;

      const computeWage = hours.totalHours
        ? totalCost / hours.totalHours
        : (hours.wage ?? 0);

      const wage = `$${computeWage.toFixed(2)}`;
      if (Number.isNaN(totalCost)) totalCost = 0;
      totalOfTotals += totalCost;
      // only approved selected and the task has approved hours      OR
      // only unapproved selected and the task has unapproved hours  OR  both selected
      const showApprovalFilters = settings.enableTimecardSubmission;
      const {
        approved: approvedEnabled,
        unapproved: unapprovedEnabled,
      } = enabledFilters;
      const hasApprovedHours = approvedEnabled && hours.approved > 0;

      const hasUnapprovedHours = unapprovedEnabled && hours.unapproved > 0;

      if (!showApprovalFilters || hasApprovedHours || hasUnapprovedHours || (approvedEnabled && unapprovedEnabled)) {
        data.push({
          name: obj.name,
          employeeId: obj.employeeId,
          date: hours.date,
          division: hours.division,
          wage,
          project: hours.project,
          costcode: hours.costcode,
          breakHours: hours.breakHours.toFixed(2),
          regularHours: hours.regularHours.toFixed(2),
          otHours: hours.regOT.toFixed(2),
          doubleOT: hours.doubleOT.toFixed(2),
          ptoHours: hours.ptoHours.toFixed(2),
          saturday: hours.saturday.toFixed(2),
          sunday: hours.sunday.toFixed(2),
          satOTHours: hours.satOTHours.toFixed(2),
          sunOTHours: hours.sunOTHours.toFixed(2),
          totalHours: hours.totalHours.toFixed(2),
          totalCost: `$${totalCost.toFixed(2)}`,
          unapproved: hours.unapproved.toFixed(2),
          approved: hours.approved.toFixed(2),
          phase: hours.phase,
          key: hours.key,
          serviceLocation: hours.serviceLocation,
          hasNightShift: hours.hasNightShift ? 'Yes' : '-',
          breakHoursRaw: hours.breakHours,
          regHoursRaw: hours.regularHours + hours.saturday + hours.sunday,
          regularOTRaw: hours.regOT + hours.saturdayOT + hours.sundayOT,
          doubleOTRaw: hours.doubleOT + hours.saturdayDoubleOT + hours.sundayDoubleOT,
          ptoHoursRaw: hours.ptoHours,
          satHoursRaw: hours.saturday,
          sundayHoursRaw: hours.sunday,
          saturdayOTRaw: hours.saturdayOT,
          sundayOTRaw: hours.sundayOT,
          saturdayDoubleOTRaw: hours.saturdayDoubleOT,
          sundayDoubleOTRaw: hours.sundayDoubleOT,
          notes: hours.notes,
          rawNotes: hours.rawNotes,
          rawTotalCost: totalCost,
        });
      }
    });
  });

  onDataChanged(data);
  onFiltersChanged({
    users: filteredUsers,
    projects,
    costcodes,
    phases,
  });
  return data.concat([{
    name: <b>Total</b>,
    totalCost: `$${totalOfTotals.toFixed(2)}`,
    isFooter: true,
  }]);
};

export const getAllColumns = (entityOTProfileMap = {}, settings = {}) => {
  const profileSettings = Object.values(entityOTProfileMap);
  const enableManualOT = profileSettings.some((profile) => profile.enableManualOT);
  const enableNightShiftPremium = profileSettings.some((profile) => profile.enableNightShiftPremium);
  const showApprovalColumns = settings.enableTimecardSubmission;

  let allColumns = [
    ReportColumn('Employee', {
      key: 'name',
      dataIndex: 'name',
      sorter: (a, b) => a?.name?.localeCompare?.(b?.name),
      showSorterTooltip: false,
      sortDirections: ['descend', 'ascend'],
    }),
    ReportColumn('Employee ID', {
      key: 'employeeId',
      dataIndex: 'employeeId',
      sorter: (a, b) => a?.employeeId?.localeCompare?.(b?.employeeId),
      showSorterTooltip: false,
      sortDirections: ['descend', 'ascend'],
    }),
  ];
  if (config.showDivisions) {
    allColumns.push(ReportColumn('Division', {
      sorter: (a, b) => a?.division?.localeCompare?.(b?.division),
      showSorterTooltip: false,
      sortDirections: ['descend', 'ascend'],
    }));
  }
  allColumns = allColumns.concat([
    ReportColumn('Date', {
      sorter: (a, b) => {
        if (!a?.date && !b?.date) return 1;
        if (!a?.date) return -1;
        if (!b?.date) return 1;

        const dateA = moment(a.date, 'MMM Do YYYY').unix();
        const dateB = moment(b.date, 'MMM Do YYYY').unix();
        return dateA - dateB;
      },
      showSorterTooltip: false,
      sortDirections: ['descend', 'ascend'],
    }),
    ...(enableNightShiftPremium ? [
      ReportColumn('Night Shift', {
        key: 'hasNightShift',
        dataIndex: 'hasNightShift',
      }),
    ] : []),
    ReportColumn('Calculated Wage', { dataIndex: 'wage', key: 'wage' }),
    ReportColumn('Project', {
      sorter: (a, b) => a?.project?.localeCompare?.(b?.project),
      showSorterTooltip: false,
      sortDirections: ['descend', 'ascend'],
    }),
    ReportColumn('Phase'),
    ReportColumn('Cost Code', {
      key: 'costcode',
      dataIndex: 'costcode',
    }),
    ReportColumn('Service Location', {
      key: 'serviceLocation',
      dataIndex: 'serviceLocation',
    }),
    ReportColumn('Reg Hrs', {
      key: 'regularHours',
      dataIndex: 'regularHours',
    }),
    ReportColumn('Break Hrs', {
      key: 'breakHours',
      dataIndex: 'breakHours',
    }),
    ReportColumn('OT Hrs', {
      key: 'otHours',
      dataIndex: 'otHours',
    }),
    ReportColumn('Double OT Hrs', {
      key: 'doubleOT',
      dataIndex: 'doubleOT',
    }),
    ReportColumn('PTO Hrs', {
      key: 'ptoHours',
      dataIndex: 'ptoHours',
    }),
    ...(!enableManualOT ? [
      ReportColumn('Sat Reg Hrs', {
        key: 'saturday',
        dataIndex: 'saturday',
      }),
      ReportColumn('Sat OT Hrs', {
        key: 'satOTHours',
        dataIndex: 'satOTHours',
      }),
      ReportColumn('Sun Reg Hrs', {
        key: 'sunday',
        dataIndex: 'sunday',
      }),
      ReportColumn('Sun OT Hrs', {
        key: 'sunOTHours',
        dataIndex: 'sunOTHours',
      }),
    ] : []),
    ReportColumn('Total Hrs', {
      key: 'totalHours',
      dataIndex: 'totalHours',
    }),
    ReportColumn('Total Cost', {
      key: 'totalCost',
      dataIndex: 'totalCost',
    }),
    ...(showApprovalColumns ? [
      ReportColumn('Approved'),
      ReportColumn('Unapproved'),
    ] : []),
    ReportColumn('Notes', { width: 500 }),
  ]);

  return allColumns;
};

export const getColFilters = (checkedColumns = new Set(), settings = {}) => {
  const { enableManualOT, enableNightShiftPremium } = settings;
  const cols = config.showDivisions
    ? [{
      title: 'Division',
      key: 'division',
      checked: checkedColumns.has('division'),
    },
    ]
    : [];

  const baseColumns = [
    ...(enableNightShiftPremium ? [{
      title: 'Night Shift',
      key: 'hasNightShift',
      checked: checkedColumns.has('hasNightShift'),
    }] : []),
    {
      title: 'Calculated Wage',
      key: 'wage',
      checked: checkedColumns.has('wage'),
    }, {
      title: 'Hours (Regular)',
      key: 'regularHours',
      checked: checkedColumns.has('regularHours'),
    }, {
      title: 'Hours (Break)',
      key: 'breakHours',
      checked: checkedColumns.has('breakHours'),
    }, {
      title: 'Hours (OT)',
      key: 'otHours',
      checked: checkedColumns.has('otHours'),
    }, {
      title: 'Hours (DT)',
      key: 'doubleOT',
      checked: checkedColumns.has('doubleOT'),
    }, {
      title: 'Hours (PTO)',
      key: 'ptoHours',
      checked: checkedColumns.has('ptoHours'),
    },
  ];

  const weekendColumns = !enableManualOT ? [{
    title: 'Sat Reg Hrs',
    key: 'saturday',
    checked: checkedColumns.has('saturday'),
  }, {
    title: 'Sat OT Hrs',
    key: 'satOTHours',
    checked: checkedColumns.has('satOTHours'),
  }, {
    title: 'Sun Reg Hrs',
    key: 'sunday',
    checked: checkedColumns.has('sunday'),
  }, {
    title: 'Sun OT Hrs',
    key: 'sunOTHours',
    checked: checkedColumns.has('sunOTHours'),
  }] : [];

  const endColumns = [{
    title: 'Total Hours',
    key: 'totalHours',
    checked: checkedColumns.has('totalHours'),
  }, {
    title: 'Total Cost',
    key: 'totalCost',
    checked: checkedColumns.has('totalCost'),
  }, {
    title: 'Notes',
    key: 'notes',
    checked: checkedColumns.has('notes'),
  }];

  return cols.concat(baseColumns, weekendColumns, endColumns);
};
